import { RootState } from 'common/redux'
import { YouTubeStreamState } from './model'

export type YouTubeStreamOffsetRequested = {
	request: {
		meRequesting: boolean
		requested: boolean
		requestTimestamp: number | undefined
	}
	answer: {
		answered: boolean
		answerTimestamp: number | undefined
		ignoreFurtherOffsetChanges: boolean
	}
}

export type YouTubeStreamUserReadiness = {
	ready: boolean
}

export type YouTubeStreamingState = {
	userReadiness: {
		ready: boolean
	}
	youtubeStreamState?: YouTubeStreamState
	offsetRequested: YouTubeStreamOffsetRequested
}

const initialState: YouTubeStreamingState = {
	userReadiness: {
		ready: false,
	},
	offsetRequested: {
		request: {
			meRequesting: false,
			requested: false,
			requestTimestamp: undefined,
		},
		answer: {
			answered: false,
			answerTimestamp: undefined,
			ignoreFurtherOffsetChanges: false,
		},
	},
}

type YouTubeStreamingAction =
	| {
			type: 'SSE_YOUTUBE_CHOSEN'
			payload: { domainEvent: YouTubeStreamState }
	  }
	| {
			type: 'SSE_YOUTUBE_STREAM_REMOVED'
			payload: { id: string }
	  }
	| {
			type: 'SSE_YOUTUBE_OFFSET_REQUESTED'
			payload: { videoId: string }
	  }
	| {
			type: 'SSE_YOUTUBE_OFFSET_RECEIVED'
			payload: { domainEvent: YouTubeStreamState }
	  }
	| {
			type: 'SSE_YOUTUBE_STREAMING_STARTED'
			payload: { domainEvent: YouTubeStreamState }
	  }
	| {
			type: 'SSE_YOUTUBE_STREAMING_PAUSED'
			payload: { domainEvent: YouTubeStreamState }
	  }
	| {
			type: 'SSE_YOUTUBE_STREAMING_FINISHED'
			payload: { domainEvent: YouTubeStreamState }
	  }
	| { type: 'YOUTUBE_STREAMING_OFFSET_PROVIDED' }
	| { type: 'YOUTUBE_STREAMING_I_REQUESTED_OFFSET' }
	| { type: 'YOUTUBE_STREAMING_READY_TO_PLAY' }

export function reducer(
	state: YouTubeStreamingState = initialState,
	action: YouTubeStreamingAction
): YouTubeStreamingState {
	switch (action.type) {
		case 'SSE_YOUTUBE_CHOSEN': {
			if (state.offsetRequested.answer.ignoreFurtherOffsetChanges) {
				return state
			}
			return {
				...state,
				youtubeStreamState: action.payload.domainEvent,
			}
		}
		case 'SSE_YOUTUBE_STREAM_REMOVED': {
			return {
				...state,
				youtubeStreamState: undefined,
			}
		}
		case 'SSE_YOUTUBE_OFFSET_REQUESTED': {
			if (state.offsetRequested.request.meRequesting) {
				return state
			}
			return {
				...state,
				offsetRequested: {
					request: {
						...state.offsetRequested.request,
						requested: true,
						requestTimestamp: Date.now(),
					},
					answer: {
						ignoreFurtherOffsetChanges: false,
						answered: false,
						answerTimestamp: undefined,
					},
				},
			}
		}
		case 'SSE_YOUTUBE_OFFSET_RECEIVED': {
			if (state.offsetRequested.request.meRequesting) {
				return {
					...state,
					userReadiness: {
						ready: true,
					},
					youtubeStreamState: action.payload.domainEvent,
					offsetRequested: {
						request: {
							meRequesting: false,
							requestTimestamp: undefined,
							requested: false,
						},
						answer: {
							answered: true,
							ignoreFurtherOffsetChanges: true,
							answerTimestamp: Date.now(),
						},
					},
				}
			}
			return {
				...state,
				offsetRequested: {
					request: {
						...state.offsetRequested.request,
						requestTimestamp: undefined,
						requested: false,
					},
					answer: {
						ignoreFurtherOffsetChanges: true,
						answered: true,
						answerTimestamp: Date.now(),
					},
				},
			}
		}
		case 'SSE_YOUTUBE_STREAMING_FINISHED': {
			return {
				...state,
				youtubeStreamState: action.payload.domainEvent,
				offsetRequested: {
					...state.offsetRequested,
					answer: {
						...state.offsetRequested.answer,
						ignoreFurtherOffsetChanges: false,
					},
				},
			}
		}
		case 'SSE_YOUTUBE_STREAMING_STARTED': {
			return {
				...state,
				userReadiness: {
					ready: true,
				},
				youtubeStreamState: action.payload.domainEvent,
				offsetRequested: {
					...state.offsetRequested,
					answer: {
						...state.offsetRequested.answer,
						ignoreFurtherOffsetChanges: false,
					},
				},
			}
		}
		case 'SSE_YOUTUBE_STREAMING_PAUSED': {
			return {
				...state,
				youtubeStreamState: action.payload.domainEvent,
				offsetRequested: {
					...state.offsetRequested,
					answer: {
						...state.offsetRequested.answer,
						ignoreFurtherOffsetChanges: false,
					},
				},
			}
		}
		case 'YOUTUBE_STREAMING_I_REQUESTED_OFFSET': {
			return {
				...state,
				userReadiness: {
					ready: false,
				},
				offsetRequested: {
					request: {
						requested: true,
						requestTimestamp: Date.now(),
						meRequesting: true,
					},
					answer: {
						ignoreFurtherOffsetChanges: false,
						answered: false,
						answerTimestamp: undefined,
					},
				},
			}
		}
		case 'YOUTUBE_STREAMING_READY_TO_PLAY': {
			return {
				...state,
				userReadiness: {
					ready: true,
				},
			}
		}
		default:
			return state
	}
}

const selectYouTubeStreamState = (
	state: RootState
): YouTubeStreamState | undefined => state.youtubeStreaming.youtubeStreamState

const selectYouTubeStreamingOffsetRequested = (
	state: RootState
): YouTubeStreamOffsetRequested => state.youtubeStreaming.offsetRequested

const selectYouTubeStreamingUserReadiness = (
	state: RootState
): YouTubeStreamUserReadiness => state.youtubeStreaming.userReadiness

const readyToPlayYouTubeStream = (): YouTubeStreamingAction => ({
	type: 'YOUTUBE_STREAMING_READY_TO_PLAY',
})

const provideOffset = (): YouTubeStreamingAction => ({
	type: 'YOUTUBE_STREAMING_OFFSET_PROVIDED',
})

const iRequestOffset = (): YouTubeStreamingAction => ({
	type: 'YOUTUBE_STREAMING_I_REQUESTED_OFFSET',
})

export { provideOffset, iRequestOffset, readyToPlayYouTubeStream }

export {
	selectYouTubeStreamState,
	selectYouTubeStreamingOffsetRequested,
	selectYouTubeStreamingUserReadiness,
}

export const redux = {
	reducer,
}
